// src/admin/components/AdminLogin.jsx
import React, { useState } from 'react';
import { 
  FaUser, 
  FaLock, 
  FaEye, 
  FaEyeSlash, 
  FaShieldAlt,
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaEnvelope,
  FaKey
} from 'react-icons/fa';
import { useAdminAuth } from '../context/AdminAuthContext';
import { LOGO_URL } from '../../constants/branding';

const AdminLogin = ({ onLoginSuccess }) => {
  const { login, verifyMFA, mfaRequired, mfaChallenge } = useAdminAuth();
  
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [mfaCode, setMfaCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleMfaChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only numbers, max 6 digits
    setMfaCode(value);
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!credentials.email || !credentials.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await login(credentials.email, credentials.password);

      if (result.success) {
        setLoginAttempts(0);
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          onLoginSuccess(result.user);
        }, 1000);
      } else if (result.requiresMFA) {
        // MFA required - show MFA input
        setSuccess('Verification code sent to your email');
        setLoading(false);
      } else {
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        
        if (newAttempts >= 5) {
          setError('Too many failed attempts. Please try again later.');
          setTimeout(() => setLoginAttempts(0), 15 * 60 * 1000);
        } else {
          setError(result.error || 'Invalid credentials. Please try again.');
        }
        setLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    
    if (!mfaCode || mfaCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await verifyMFA(mfaCode);

      if (result.success) {
        setLoginAttempts(0);
        setSuccess('Verification successful! Redirecting...');
        setTimeout(() => {
          onLoginSuccess(result.user);
        }, 1000);
      } else {
        setError(result.error || 'Invalid verification code. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('MFA verification error:', error);
      setError('Verification failed. Please try again.');
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    // Re-initiate login to get a new code
    setMfaCode('');
    setError('');
    setSuccess('Requesting new verification code...');
    await handleSubmit({ preventDefault: () => {} });
  };

  const isLockedOut = loginAttempts >= 5;

  // MFA Verification Screen
  if (mfaRequired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#282828] via-[#1d2021] to-[#3c3836] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <div className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 bg-gradient-to-r from-[#FFD700] to-[#FFB700] shadow-lg">
                <img src={LOGO_URL} alt="Peaknet" className="h-10 w-auto block select-none" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-[#ebdbb2] mb-2">Peaknet Admin</h1>
            <p className="text-[#bdae93]">Multi-Factor Authentication</p>
          </div>

          <div className="bg-[#1d2021] border border-[#3c3836] rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#3c3836] rounded-full mb-4 border border-[#504945]">
                <FaEnvelope className="text-[#fabd2f] text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-[#ebdbb2] mb-2">Verify Your Identity</h2>
              <p className="text-[#bdae93]">
                We've sent a verification code to<br />
                <span className="font-semibold text-[#ebdbb2]">
                  {mfaChallenge?.destination || credentials.email}
                </span>
              </p>
            </div>

            <form onSubmit={handleMfaSubmit} className="space-y-6">
              <div>
                <label htmlFor="mfaCode" className="block text-sm font-medium text-[#ebdbb2] mb-2">
                  Verification Code
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaKey className="h-5 w-5 text-[#7c6f64]" />
                  </div>
                  <input
                    id="mfaCode"
                    name="mfaCode"
                    type="text"
                    value={mfaCode}
                    onChange={handleMfaChange}
                    disabled={loading}
                    className="block w-full pl-10 pr-3 py-3 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent disabled:bg-[#3c3836] disabled:text-[#7c6f64] disabled:cursor-not-allowed text-center text-2xl tracking-widest font-mono"
                    placeholder="000000"
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </div>
                <p className="mt-2 text-sm text-[#bdae93]">
                  Enter the 6-digit code from your email
                </p>
              </div>

              {success && (
                <div className="flex items-center space-x-2 p-3 bg-[#3c3836] border border-[#b8bb26] rounded-lg">
                  <FaCheckCircle className="text-[#b8bb26] flex-shrink-0" />
                  <p className="text-sm text-[#d5e39f]">{success}</p>
                </div>
              )}

              {error && (
                <div className="flex items-center space-x-2 p-3 bg-[#3c3836] border border-[#fb4934] rounded-lg">
                  <FaExclamationTriangle className="text-[#fb4934] flex-shrink-0" />
                  <p className="text-sm text-[#fb8071]">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || mfaCode.length !== 6}
                className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-lg text-[#1d2021] bg-[#fabd2f] hover:bg-[#fe8019] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#fabd2f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <FaShieldAlt className="-ml-1 mr-3 h-5 w-5" />
                    Verify Code
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                className="w-full text-sm text-[#fabd2f] hover:text-[#fe8019] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Didn't receive the code? Resend
              </button>
            </form>
          </div>

          <div className="text-center mt-6">
            <p className="text-[#bdae93] text-sm">
              © 2025 Peaknet. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Login Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#282828] via-[#1d2021] to-[#3c3836] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <div className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 bg-gradient-to-r from-[#FFD700] to-[#FFB700] shadow-lg">
              <img src={LOGO_URL} alt="Peaknet" className="h-10 w-auto block select-none" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-[#ebdbb2] mb-2">Peaknet Admin</h1>
          <p className="text-[#bdae93]">Secure Administrative Access</p>
        </div>

        {/* Login Form */}
        <div className="bg-[#1d2021] border border-[#3c3836] rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-[#ebdbb2] mb-2">Admin Login</h2>
            <p className="text-sm text-[#bdae93]">Enter your credentials to access the admin portal</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#ebdbb2] mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser className="h-5 w-5 text-[#7c6f64]" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={credentials.email}
                  onChange={handleInputChange}
                  disabled={isLockedOut || loading}
                  className="block w-full pl-10 pr-3 py-3 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent disabled:bg-[#3c3836] disabled:text-[#7c6f64] disabled:cursor-not-allowed"
                  placeholder="admin@peaknet.com.au"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#ebdbb2] mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-[#7c6f64]" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={handleInputChange}
                  disabled={isLockedOut || loading}
                  className="block w-full pl-10 pr-12 py-3 border border-[#504945] rounded-lg bg-[#3c3836] text-[#ebdbb2] placeholder-[#7c6f64] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent disabled:bg-[#3c3836] disabled:text-[#7c6f64] disabled:cursor-not-allowed"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLockedOut || loading}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#7c6f64] hover:text-[#ebdbb2] disabled:cursor-not-allowed"
                >
                  {showPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {success && (
              <div className="flex items-center space-x-2 p-3 bg-[#3c3836] border border-[#b8bb26] rounded-lg">
                <FaCheckCircle className="text-[#b8bb26] flex-shrink-0" />
                <p className="text-sm text-[#d5e39f]">{success}</p>
              </div>
            )}

            {error && (
              <div className="flex items-center space-x-2 p-3 bg-[#3c3836] border border-[#fb4934] rounded-lg">
                <FaExclamationTriangle className="text-[#fb4934] flex-shrink-0" />
                <p className="text-sm text-[#fb8071]">{error}</p>
              </div>
            )}

            {loginAttempts > 0 && loginAttempts < 5 && (
              <div className="flex items-center space-x-2 p-3 bg-[#3c3836] border border-[#fabd2f] rounded-lg">
                <FaExclamationTriangle className="text-[#fabd2f] flex-shrink-0" />
                <p className="text-sm text-[#ebdbb2]">
                  Failed attempts: {loginAttempts}/5. Account will be locked after 5 failed attempts.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLockedOut || loading}
              className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-lg text-[#1d2021] bg-[#fabd2f] hover:bg-[#fe8019] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#fabd2f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin -ml-1 mr-3 h-5 w-5" />
                  Authenticating...
                </>
              ) : (
                <>
                  <FaShieldAlt className="-ml-1 mr-3 h-5 w-5" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Security Notice */}
          <div className="mt-6 p-4 bg-[#282828] rounded-lg border border-[#3c3836]">
            <div className="flex items-start space-x-3">
              <FaShieldAlt className="text-[#fabd2f] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-[#bdae93]">
                  <strong>Security Notice:</strong> This portal uses Multi-Factor Authentication (MFA) 
                  for enhanced security. All login attempts are logged and monitored.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-[#bdae93] text-sm">
            © 2025 Peaknet. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
